import type { CompositionLegend, CompositionTypography } from './model';
import type { LegendTextMeasurement } from './legendLayout';
import { loadPdfFontBytes, pdfFontKey } from './export/vectorPdf/fonts';

type MeasuredFont = {
  layout(text: string): {
    positions: readonly { xAdvance: number }[];
    glyphs: readonly { advanceWidth: number }[];
  };
  unitsPerEm: number;
};
const fonts = new Map<string, Promise<MeasuredFont>>();

/** Reuse shipped TTFs and fontkit; cache only font variants, never project content. */
export async function loadLegendMeasurement(
  legend: Pick<CompositionLegend, 'bodyTypography' | 'heading'>,
  loadBytes = loadPdfFontBytes,
): Promise<LegendTextMeasurement> {
  const { default: fontkit } = await import('@pdf-lib/fontkit');
  const variants = [
    legend.bodyTypography,
    { ...legend.bodyTypography, fontWeight: 'bold' as const },
    ...(legend.heading.enabled ? [legend.heading.typography] : []),
  ];
  const loaded = new Map<string, MeasuredFont>();
  const local = new Map<string, Promise<MeasuredFont>>();
  await Promise.all(
    variants.map(async (type) => {
      const key = pdfFontKey(type);
      // Injected loaders own their resources (unit tests/export dependency injection).
      let pending = (loadBytes === loadPdfFontBytes ? fonts.get(key) : undefined) ?? local.get(key);
      if (!pending) {
        pending = loadBytes(key).then((bytes) => fontkit.create(bytes));
        if (loadBytes === loadPdfFontBytes) {
          fonts.set(key, pending);
          void pending.catch(() => {
            if (fonts.get(key) === pending) fonts.delete(key);
          });
        }
      }
      local.set(key, pending);
      loaded.set(key, await pending);
    }),
  );
  const widths = new Map<string, number>();
  return (text: string, typography: CompositionTypography) => {
    const key = `${pdfFontKey(typography)}:${text}`;
    let em = widths.get(key);
    if (em === undefined) {
      const font = loaded.get(pdfFontKey(typography));
      if (!font) throw new Error('Legend font variant was not measured.');
      const run = font.layout(text.replace(/[\r\n\t]/g, ' '));
      // PDF embedding uses glyph advances; include both advances and positioned browser widths.
      // This conservative common width prevents kerning from forcing PDF text compression.
      em =
        Math.max(
          run.positions.reduce((sum, position) => sum + position.xAdvance, 0),
          run.glyphs.reduce((sum, glyph) => sum + glyph.advanceWidth, 0),
        ) / font.unitsPerEm;
      widths.set(key, em);
      // Bounded per-measurer cache: repeated project/label edits cannot retain unbounded text.
      if (widths.size > 2048) {
        widths.clear();
        widths.set(key, em);
      }
    }
    return em * typography.fontSizePt * (25.4 / 72);
  };
}
